import type { IconName } from './ui/Icon.js';

/** Primary navigation model, shared by the desktop sidebar and mobile tab bar. */
export interface NavItem {
  to: string;
  label: string;
  short: string;
  icon: IconName;
}

/**
 * Primary nav (spec 2026-10-14-workspaces): the always-visible top of the
 * sidebar. Projects is a main item (owner: "not under More"); every
 * workspace (job) follows in its own section, then the grouped rest.
 */
export const NAV: readonly NavItem[] = [
  { to: '/', label: 'Today', short: 'Today', icon: 'sun' },
  { to: '/inbox', label: 'Inbox', short: 'Inbox', icon: 'inbox' },
  { to: '/tasks', label: 'Action items', short: 'Items', icon: 'tasks' },
  { to: '/calendar', label: 'Calendar', short: 'Calendar', icon: 'calendar' },
  { to: '/people', label: 'People', short: 'People', icon: 'users' },
  { to: '/projects', label: 'Projects', short: 'Projects', icon: 'folder' },
];

/** The phone tab bar: four destinations, Capture in the middle, then Search. */
export const TABS: readonly NavItem[] = [
  { to: '/', label: 'Today', short: 'Today', icon: 'sun' },
  { to: '/inbox', label: 'Inbox', short: 'Inbox', icon: 'inbox' },
  { to: '/tasks', label: 'Action items', short: 'Items', icon: 'tasks' },
  { to: '/projects', label: 'Projects', short: 'Projects', icon: 'folder' },
];

/** The tab bar's center slot sits after this many TABS. */
export const CAPTURE_SLOT = 2;

/** A sidebar section below the workspaces; `collapsible` groups fold. */
export interface SideSection {
  key: string;
  title: string | null;
  icon?: IconName;
  collapsible: boolean;
  links: readonly NavItem[];
}

const link = (to: string, label: string, icon: IconName): NavItem => ({ to, label, short: label, icon });

/**
 * Hubs (owner 2026-11-02, "dumb down the layout"): related pages share one
 * sidebar row and a tab strip across the top (`HubTabs`, drawn by `Page`).
 * Every page keeps its own URL; a tab may hold a second page (`subs`), shown
 * as a small switch under the strip. The first tab is the hub's sidebar link.
 */
export interface HubTab {
  to: string;
  label: string;
  /** Pages that live inside this tab, first = the tab's own page. */
  subs?: readonly { to: string; label: string }[];
}

export interface Hub {
  key: string;
  label: string;
  icon: IconName;
  tabs: readonly HubTab[];
}

export const HUBS: readonly Hub[] = [
  {
    key: 'calendar',
    label: 'Calendar',
    icon: 'calendar',
    tabs: [
      { to: '/calendar', label: 'Calendar' },
      { to: '/plan', label: 'Goals & routines' },
    ],
  },
  {
    key: 'health',
    label: 'Health',
    icon: 'heart',
    tabs: [
      { to: '/health', label: 'Today' },
      { to: '/trends', label: 'Trends' },
      {
        to: '/records',
        label: 'Records',
        subs: [
          { to: '/records', label: 'Records' },
          { to: '/concerns', label: 'Concerns' },
        ],
      },
      { to: '/journal', label: 'Journal' },
      { to: '/coach', label: 'Coach' },
    ],
  },
  {
    key: 'fitness',
    label: 'Fitness',
    icon: 'dumbbell',
    tabs: [
      { to: '/training', label: 'Training' },
      { to: '/nutrition', label: 'Food' },
    ],
  },
  {
    key: 'home',
    label: 'Home',
    icon: 'package',
    tabs: [
      {
        to: '/obligations',
        label: 'Bills & renewals',
        subs: [
          { to: '/obligations', label: 'Bills & renewals' },
          { to: '/services', label: 'Subscriptions' },
        ],
      },
      { to: '/documents', label: 'Documents' },
      { to: '/belongings', label: 'Belongings' },
      { to: '/trips', label: 'Trips' },
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: 'settings',
    tabs: [
      { to: '/settings', label: 'Settings' },
      { to: '/connections', label: 'Connections' },
      { to: '/agents', label: 'Agents' },
      { to: '/contacts', label: 'Dispatcher' },
      { to: '/admin', label: 'Admin' },
    ],
  },
];

/** Every page a hub tab reaches (tabs and their subs), once each. */
function hubPages(h: Hub): string[] {
  return [...new Set(h.tabs.flatMap((t) => [t.to, ...(t.subs ?? []).map((x) => x.to)]))];
}

const hubLink = (key: string): NavItem => {
  const h = HUBS.find((x) => x.key === key)!;
  return link(h.tabs[0]!.to, h.label, h.icon);
};

/**
 * The flat rows below the workspaces: no folding groups. Coffee stays its own
 * row (daily use); Coming up lives on Today ("N more this week") and Timeline
 * behind Today's history button.
 */
export const SIDE_SECTIONS: readonly SideSection[] = [
  {
    key: 'rest',
    title: null,
    collapsible: false,
    links: [
      link('/notes', 'Notes', 'bulb'),
      link('/money', 'Money', 'wallet'),
      hubLink('health'),
      hubLink('fitness'),
      link('/coffee', 'Coffee', 'coffee'),
      hubLink('home'),
    ],
  },
];

/** Pinned to the bottom of the sidebar. */
export const SETTINGS_LINK: NavItem = hubLink('settings');

/** Every fixed sidebar row, in order. */
export function sidebarLinks(): NavItem[] {
  return [...NAV, ...SIDE_SECTIONS.flatMap((s) => s.links), SETTINGS_LINK];
}

/** Every destination reachable from the sidebar: its rows, every hub page, and Timeline / Coming up via Today. */
export function reachableLinks(): string[] {
  return [...new Set([...sidebarLinks().map((l) => l.to), ...HUBS.flatMap(hubPages), '/timeline', '/library'])];
}

const under = (pathname: string, base: string): boolean =>
  base === '/' ? pathname === '/' : pathname === base || pathname.startsWith(`${base}/`);

/** The hub (and its tab, and the sub page) a path belongs to; null off-hub. */
export function hubFor(pathname: string): { hub: Hub; tab: HubTab; sub: string } | null {
  let best: { hub: Hub; tab: HubTab; sub: string } | null = null;
  for (const hub of HUBS)
    for (const tab of hub.tabs)
      for (const to of [tab.to, ...(tab.subs ?? []).map((x) => x.to)])
        if (under(pathname, to) && (best === null || to.length > best.sub.length)) best = { hub, tab, sub: to };
  return best;
}

/** One row on the More hub. */
export interface LibraryLink {
  to: string;
  label: string;
  hint: string;
  icon: IconName;
  /** Icon tile colour (Settings-style). */
  tint: string;
}

export interface LibrarySection {
  title: string;
  links: readonly LibraryLink[];
}

/**
 * Everything off the primary nav, grouped for the More hub. Every `to` is a
 * registered route in App.tsx (nav.test.tsx checks it).
 */
export const LIBRARY: readonly LibrarySection[] = [
  {
    title: 'Health',
    links: [
      { to: '/health', label: 'Health today', hint: 'vitals, sleep, quick log', icon: 'heart', tint: '#ff375f' },
      { to: '/nutrition', label: 'Food', hint: 'meals, macros', icon: 'utensils', tint: '#ff9f0a' },
      { to: '/coffee', label: 'Coffee', hint: 'espresso shots, caffeine', icon: 'coffee', tint: '#a2845e' },
      { to: '/training', label: 'Training', hint: 'plans, workouts', icon: 'dumbbell', tint: '#30b0c7' },
      { to: '/trends', label: 'Trends', hint: 'charts', icon: 'trend', tint: '#0a84ff' },
      { to: '/records', label: 'Records', hint: 'labs, notes, medications', icon: 'clipboard', tint: '#5e5ce6' },
      { to: '/concerns', label: 'Concerns', hint: 'health episodes', icon: 'alert', tint: '#ff453a' },
      { to: '/journal', label: 'Journal', hint: 'how you feel', icon: 'book', tint: '#bf5af2' },
      { to: '/coach', label: 'Coach', hint: 'ask about your health', icon: 'chat', tint: '#0a7c72' },
      { to: '/plan', label: 'Plan', hint: 'goals, routines, calendar', icon: 'target', tint: '#34c759' },
    ],
  },
  {
    title: 'Money',
    links: [{ to: '/money', label: 'Money', hint: 'accounts, cash flow, budget, income', icon: 'wallet', tint: '#248a3d' }],
  },
  {
    title: 'Life',
    links: [
      { to: '/people', label: 'People', hint: 'birthdays, last contact', icon: 'users', tint: '#0a84ff' },
      { to: '/projects', label: 'Projects', hint: 'clients and work', icon: 'folder', tint: '#5e5ce6' },
      { to: '/obligations', label: 'Obligations', hint: 'bills, taxes, renewals', icon: 'receipt', tint: '#ff9f0a' },
      { to: '/services', label: 'Services', hint: 'subscriptions', icon: 'repeat', tint: '#30b0c7' },
      { to: '/documents', label: 'Documents', hint: 'IDs, cards, expiries', icon: 'file', tint: '#8e8e93' },
      { to: '/belongings', label: 'Belongings', hint: 'warranties, loans', icon: 'package', tint: '#a2845e' },
      { to: '/trips', label: 'Trips', hint: 'plans and packing', icon: 'plane', tint: '#0a7c72' },
      { to: '/notes', label: 'Ideas & notes', hint: 'captured thoughts', icon: 'bulb', tint: '#ffcc00' },
      { to: '/timeline', label: 'Timeline', hint: 'what happened when', icon: 'history', tint: '#636366' },
    ],
  },
  {
    title: 'System',
    links: [
      { to: '/agents', label: 'Agents', hint: 'approvals, runs, policies', icon: 'bot', tint: '#5856d6' },
      { to: '/connections', label: 'Connections', hint: 'Claude, tokens, accounts', icon: 'plug', tint: '#34c759' },
      { to: '/contacts', label: 'Contacts', hint: 'who the dispatcher hears', icon: 'contact', tint: '#ff9500' },
      { to: '/settings', label: 'Settings', hint: 'profile, data sources', icon: 'settings', tint: '#8e8e93' },
      { to: '/admin', label: 'Admin', hint: 'system health', icon: 'shield', tint: '#636366' },
    ],
  },
];

/**
 * Which sidebar destination a path lights: the longest matching `to` among the
 * fixed links. Workspace pages (/w/:key) light their workspace row instead
 * (handled by the sidebar), so they return null here. /brief/:id, /library and
 * /timeline → Today; a hub page → its hub's row.
 */
export function activeNav(pathname: string): string | null {
  if (pathname.startsWith('/brief/')) return '/';
  // Off the sidebar since 2026-11-02, reached from Today.
  if (under(pathname, '/library') || under(pathname, '/timeline')) return '/';
  // A hub page lights its hub's row (Concerns → Health, Agents → Settings).
  const hub = hubFor(pathname);
  if (hub) return hub.hub.tabs[0]!.to;
  let best: string | null = null;
  for (const l of sidebarLinks()) {
    if (under(pathname, l.to) && (best === null || l.to.length > best.length)) best = l.to;
  }
  return best;
}

/** Which phone tab a path lights (workspaces count as Projects). */
export function activeTab(pathname: string): string | null {
  if (pathname.startsWith('/w/')) return '/projects';
  if (pathname.startsWith('/brief/')) return '/';
  return TABS.find((t) => under(pathname, t.to))?.to ?? null;
}
